import { useId, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import type {
  CarePathwayTemplateViewModel,
} from '@/features/patient-record/types';

import type { TemplateInput, TemplateStepInput } from '../hooks';

import { StepEditorList } from './StepEditorList';

interface TemplateEditorDialogProps {
  readonly mode: 'create' | 'edit';
  readonly initial: CarePathwayTemplateViewModel | null;
  readonly busy: boolean;
  readonly errorMessage: string | null;
  readonly onCancel: () => void;
  readonly onSubmit: (input: TemplateInput) => void;
}

/**
 * Shared create + edit form for care-pathway templates.
 *
 * Snapshot semantics: on edit, the BE leaves existing enrollments alone —
 * they keep the step list they were enrolled against. The banner at the
 * top of the editor surfaces that so admins don't expect retroactive
 * propagation.
 */
export function TemplateEditorDialog({
  mode,
  initial,
  busy,
  errorMessage,
  onCancel,
  onSubmit,
}: TemplateEditorDialogProps): ReactElement {
  const { t } = useTranslation();
  const nameId = useId();
  const descId = useId();

  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [steps, setSteps] = useState<readonly TemplateStepInput[]>(
    () =>
      initial?.steps.map((s, idx) => ({
        id: s.id,
        name: s.name,
        description: s.description ?? '',
        sortOrder: idx,
      })) ?? [],
  );

  const trimmedName = name.trim();
  const stepsValid = steps.every((s) => s.name.trim().length > 0);
  const canSubmit =
    !busy && trimmedName.length > 0 && steps.length > 0 && stepsValid;

  const handleSubmit = (event: { readonly preventDefault: () => void }): void => {
    event.preventDefault();
    if (!canSubmit) return;
    onSubmit({
      name: trimmedName,
      description: description.trim(),
      steps: steps.map((s, idx) => ({
        ...(s.id !== undefined ? { id: s.id } : {}),
        name: s.name.trim(),
        description: s.description.trim(),
        sortOrder: idx,
      })),
    });
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !busy) onCancel();
      }}
    >
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" disableOutsideClose>
        <DialogHeader>
          <DialogTitle>
            {mode === 'create'
              ? t('routes.admin.carePathways.createTitle')
              : t('routes.admin.carePathways.editTitle')}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? t('routes.admin.carePathways.createDescription')
              : t('routes.admin.carePathways.editDescription')}
          </DialogDescription>
        </DialogHeader>

        {mode === 'edit' && (
          <div className="flex items-start gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-[12px] text-foreground">
            <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" aria-hidden="true" />
            <p>{t('routes.admin.carePathways.snapshotBanner')}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <label
              htmlFor={nameId}
              className="block text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted-foreground"
            >
              {t('routes.admin.carePathways.nameLabel')}
              <span className="text-destructive ml-0.5">*</span>
            </label>
            <Input
              id={nameId}
              type="text"
              value={name}
              placeholder={t('routes.admin.carePathways.namePlaceholder')}
              onChange={(event) => {
                setName(event.target.value);
              }}
              disabled={busy}
              className="h-9"
            />
          </div>

          <div className="space-y-1.5">
            <label
              htmlFor={descId}
              className="block text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted-foreground"
            >
              {t('routes.admin.carePathways.descriptionLabel')}
            </label>
            <Textarea
              id={descId}
              value={description}
              rows={2}
              placeholder={t('routes.admin.carePathways.descriptionPlaceholder')}
              onChange={(event) => {
                setDescription(event.target.value);
              }}
              disabled={busy}
            />
          </div>

          <div className="space-y-1.5">
            <span className="block text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
              {t('routes.admin.carePathways.stepsLabel')}
              <span className="text-destructive ml-0.5">*</span>
            </span>
            <StepEditorList steps={steps} onChange={setSteps} disabled={busy} />
          </div>

          {errorMessage !== null && (
            <div
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
            >
              {errorMessage}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onCancel} disabled={busy}>
              {t('ui.dialog.cancel')}
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {busy
                ? t('routes.admin.carePathways.saving')
                : mode === 'create'
                  ? t('routes.admin.carePathways.create')
                  : t('routes.admin.carePathways.save')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
